"use client";

import React, { CSSProperties, ReactNode } from "react";

import { useRotatingBanners } from "@/hooks/banners/useRotatingBanners";
import {
  BannerTransitionType,
  getBannerTransitionContainerClass,
  getBannerTransitionEnterClass,
  getBannerTransitionExitClass,
} from "@/lib/banners/bannerTransition";
import { PublicBannerItem } from "@/lib/banners/types";
import { cn } from "@/lib/utils";

const DEFAULT_TRANSITION_DURATION_MS = 600;

const hasOwnCta = (banner: PublicBannerItem) => Boolean(banner.ctaConfig?.enabled);

/**
 * Resolves which banner's CTA should be shown over the currently active banner.
 *
 * - If the active banner specifies its own CTA, that one wins.
 * - Otherwise, if the whole slot contains exactly one banner with a CTA, that single CTA
 *   is shown across all banners (so it persists while images rotate).
 * - Otherwise (0 or 2+ CTAs and the active one has none), no CTA is shown.
 */
const resolveCtaBanner = (activeBanner: PublicBannerItem, banners: PublicBannerItem[]): PublicBannerItem | null => {
  if (hasOwnCta(activeBanner)) {
    return activeBanner;
  }

  const bannersWithCta = banners.filter(hasOwnCta);
  return bannersWithCta.length === 1 ? bannersWithCta[0] : null;
};

export type RotatingBannerRenderMeta = {
  isActive: boolean;
  withKenBurns: boolean;
};

export type RotatingBannerProps = {
  banners: PublicBannerItem[];
  /**
   * Render function for the currently active banner. The second argument is the banner whose
   * CTA should be displayed (the active banner's own CTA, the slot's sole CTA, or null).
   * The third argument provides rotation metadata (e.g. whether Ken Burns should apply).
   */
  children: (
    banner: PublicBannerItem,
    ctaBanner: PublicBannerItem | null,
    meta?: RotatingBannerRenderMeta,
  ) => ReactNode;
  intervalMs?: number;
  transitionDurationMs?: number;
  /** CSS-only transition between banners. Ignored when there is only one banner. */
  transition?: BannerTransitionType;
  className?: string;
  /** If true, adds a slow zoom/pan effect to the active banner image. */
  withKenBurns?: boolean;
};

const RotatingBanner = ({
  banners,
  children,
  intervalMs,
  transitionDurationMs = DEFAULT_TRANSITION_DURATION_MS,
  transition = "crossfade",
  className,
  withKenBurns = false,
}: RotatingBannerProps) => {
  const { activeIndex, outgoingIndex, incomingIndex, isTransitioning, isRotating } = useRotatingBanners({
    banners,
    intervalMs,
    transitionDurationMs,
  });

  const activeBanner = banners[activeIndex] ?? banners[0] ?? null;

  if (!activeBanner) {
    return null;
  }

  const renderSlide = (banner: PublicBannerItem, meta: RotatingBannerRenderMeta) =>
    children(banner, resolveCtaBanner(banner, banners), meta);

  if (!isRotating) {
    return <>{renderSlide(activeBanner, { isActive: true, withKenBurns })}</>;
  }

  const transitionAnimationStyle: CSSProperties = {
    animationDuration: `${transitionDurationMs}ms`,
    animationFillMode: "both",
  };

  const transitionLayerClassName =
    transition === "flip" ? "[backface-visibility:hidden] [transform-style:preserve-3d]" : "";

  const containerClassName = cn(
    "relative w-full",
    getBannerTransitionContainerClass(transition),
    withKenBurns && "overflow-hidden",
    className,
  );

  return (
    <div className={containerClassName}>
      {/* In-flow ghost establishes height for absolutely positioned transition layers */}
      <div className="pointer-events-none invisible" aria-hidden>
        {renderSlide(activeBanner, { isActive: false, withKenBurns: false })}
      </div>

      {isTransitioning && outgoingIndex !== null && incomingIndex !== null ? (
        <>
          <div
            key={`out-${banners[outgoingIndex].bannerId}`}
            className={cn("absolute inset-0 z-0", transitionLayerClassName, getBannerTransitionExitClass(transition))}
            style={transitionAnimationStyle}
            aria-hidden
          >
            {renderSlide(banners[outgoingIndex], { isActive: false, withKenBurns: false })}
          </div>
          <div
            key={`in-${banners[incomingIndex].bannerId}`}
            className={cn("absolute inset-0 z-10", transitionLayerClassName, getBannerTransitionEnterClass(transition))}
            style={transitionAnimationStyle}
          >
            {renderSlide(banners[incomingIndex], { isActive: true, withKenBurns })}
          </div>
        </>
      ) : (
        <div className="absolute inset-0">{renderSlide(activeBanner, { isActive: true, withKenBurns })}</div>
      )}
    </div>
  );
};

export default RotatingBanner;
